import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { convertDataToISO, convertISOToData, maskCNPJ, maskCPF, maskData, unmaskCNPJ, unmaskCPF } from "@/lib/utils";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { CriarUsuarioFormData } from "../schemas/usuario.schema";

interface PessoaProps {
  form: UseFormReturn<CriarUsuarioFormData>;
  formData: CriarUsuarioFormData;
  readOnly?: boolean;
}

export const Pessoa = ({ form, formData, readOnly = false }: PessoaProps) => {
  const [cnpjErro, setCnpjErro] = useState<string>("");
  const [verificandoCnpj, setVerificandoCnpj] = useState(false);
  const [dataNascimentoFormatada, setDataNascimentoFormatada] = useState<string>("");
  const [dataNascimentoErro, setDataNascimentoErro] = useState<string>("");
  const [rgErro, setRgErro] = useState<string>("");
  
  // Observa mudanças no tipo em tempo real
  const tipoSelecionado = form.watch("tipo");

  // Carrega a data de nascimento formatada quando o formData muda (ao editar)
  useEffect(() => {
    if (formData.dataNascimento) {
      const dataFormatada = convertISOToData(formData.dataNascimento);
      setDataNascimentoFormatada(dataFormatada);
    } else {
      setDataNascimentoFormatada("");
    }
  }, [formData.dataNascimento]);
  
  const verificarCnpj = async (cnpj: string) => {
    if (!cnpj || cnpj.trim() === "" || cnpj.replace(/\D/g, "").length < 14) {
      setCnpjErro("");
      return;
    }

    setVerificandoCnpj(true);
    setCnpjErro("");

    try {
      const response = await api.post(`/api/usuario/verificar-cnpj`, {
        cnpj: cnpj
      });

      if (!response.result?.disponivel) {
        setCnpjErro("Este CNPJ já está cadastrado no sistema");
      }
    } catch (error: any) {
      console.error("Erro ao verificar CNPJ:", error);
      setCnpjErro("Erro ao verificar disponibilidade");
    } finally {
      setVerificandoCnpj(false);
    }
  };

  // Limpa campos quando o tipo muda
  const handleTipoChange = (novoTipo: "FISICA" | "JURIDICA") => {
    form.setValue("tipo", novoTipo);
    
    // Limpa campos do tipo anterior
    if (novoTipo === "FISICA") {
      form.setValue("cnpj", "");
      form.setValue("nomeFantasia", "");
      form.setValue("inscricaoEstadual", "");
      setCnpjErro("");
    } else {
      form.setValue("cpf", "");
      form.setValue("rg", "");
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dados Pessoais</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="nome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Nome Completo <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Digite o nome completo"
                    disabled={readOnly}
                    readOnly={readOnly}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="tipo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo</FormLabel>
                <Select 
                  onValueChange={(value) => {
                    handleTipoChange(value as "FISICA" | "JURIDICA");
                    field.onChange(value);
                  }} 
                  value={field.value || "FISICA"}
                  disabled={readOnly}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="FISICA">Pessoa Física</SelectItem>
                    <SelectItem value="JURIDICA">Pessoa Jurídica</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="categoria"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categoria</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value} disabled={readOnly}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="PACIENTE">Paciente</SelectItem>
                  <SelectItem value="MEDICO">Médico</SelectItem>
                  <SelectItem value="FUNCIONARIO">Funcionário</SelectItem>
                  <SelectItem value="ADMIN">Administrador</SelectItem>
                  <SelectItem value="EMPRESA">Empresa</SelectItem>
                  <SelectItem value="OUTRO">Outro</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {tipoSelecionado === "FISICA" && (
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="cpf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    CPF <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={maskCPF(field.value || "")}
                      onChange={(e) => field.onChange(unmaskCPF(e.target.value))}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      disabled={readOnly}
                      readOnly={readOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="rg"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RG</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className={rgErro ? "border-red-500" : ""}
                      placeholder="00.000.000-0"
                      maxLength={20}
                      disabled={readOnly}
                      readOnly={readOnly}
                      onChange={(e) => {
                        field.onChange(e);
                        setRgErro("");
                      }}
                      onBlur={(e) => {
                        const valor = e.target.value.trim();
                        if (valor && valor.length < 5) {
                          setRgErro("RG deve ter pelo menos 5 caracteres");
                        } else {
                          setRgErro("");
                        }
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                  {rgErro && (
                    <p className="text-sm text-red-500">{rgErro}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {tipoSelecionado === "JURIDICA" && (
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="cnpj"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    CNPJ <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={maskCNPJ(field.value || "")}
                      className={cnpjErro ? "border-red-500" : ""}
                      disabled={verificandoCnpj || readOnly}
                      readOnly={readOnly}
                      onChange={(e) => {
                        field.onChange(unmaskCNPJ(e.target.value));
                        setCnpjErro("");
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        verificarCnpj(e.target.value);
                      }}
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                    />
                  </FormControl>
                  {cnpjErro && (
                    <p className="text-sm text-red-500">{cnpjErro}</p>
                  )}
                  {verificandoCnpj && (
                    <p className="text-sm text-muted-foreground">Verificando disponibilidade...</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nomeFantasia"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Fantasia</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Nome fantasia"
                      disabled={readOnly}
                      readOnly={readOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="inscricaoEstadual"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inscrição Estadual</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Inscrição Estadual"
                      disabled={readOnly}
                      readOnly={readOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="dataNascimento"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data de Nascimento</FormLabel>
                <FormControl>
                  <Input
                    id="dataNascimento"
                    type="text"
                    value={dataNascimentoFormatada || (field.value ? convertISOToData(field.value) : "")}
                    className={dataNascimentoErro ? "border-red-500" : ""}
                    disabled={readOnly}
                    readOnly={readOnly}
                    onChange={(e) => {
                      const valorFormatado = maskData(e.target.value);
                      setDataNascimentoFormatada(valorFormatado);
                      setDataNascimentoErro("");
                    }}
                    onBlur={(e) => {
                      const valor = e.target.value.trim();
                      
                      if (!valor || valor.length === 0) {
                        setDataNascimentoErro("");
                        field.onChange("");
                        setDataNascimentoFormatada("");
                        field.onBlur();
                        return;
                      }
                      
                      if (valor.length < 10) {
                        setDataNascimentoErro("Data incompleta. Use o formato dd/MM/yyyy");
                        field.onBlur();
                        return;
                      }
                      
                      if (valor.length === 10) {
                        const dataISO = convertDataToISO(valor);
                        if (!dataISO) {
                          setDataNascimentoErro("Data inválida. Exemplo: 31/12/1990");
                        } else {
                          setDataNascimentoErro("");
                          field.onChange(dataISO);
                        }
                      }
                      
                      field.onBlur();
                    }}
                    placeholder="dd/MM/yyyy (ex: 31/12/1990)"
                    maxLength={10}
                  />
                </FormControl>
                {dataNascimentoErro && (
                  <p className="text-sm text-red-500">{dataNascimentoErro}</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="genero"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Gênero</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ""} disabled={readOnly}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o gênero" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="MASCULINO">Masculino</SelectItem>
                    <SelectItem value="FEMININO">Feminino</SelectItem>
                    <SelectItem value="OUTRO">Outro</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};

